import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Modal from "@mui/material/Modal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

const CHAMPS_BDD = [
  { value: "", label: "— Ignorer —" },
  { value: "prenom", label: "Prénom" },
  { value: "nom", label: "Nom" },
  { value: "date_naissance", label: "Date de naissance" },
  { value: "sexe", label: "Sexe (homme/femme)" },
  { value: "situation_familiale", label: "Situation familiale" },
  { value: "adresse", label: "Adresse" },
  { value: "ville", label: "Ville" },
  { value: "code_postal", label: "Code postal" },
  { value: "telephone", label: "Téléphone" },
  { value: "email", label: "Email" },
  { value: "langue_parlee", label: "Langues parlées (séparées par ;)" },
  { value: "niveau_etudes", label: "Niveau d'études" },
  { value: "classe", label: "Classe" },
  { value: "profession", label: "Profession" },
  { value: "niveau_quoran", label: "Niveau Qour'an" },
  { value: "niveau_arabe", label: "Niveau arabe" },
  { value: "sait_lire_arabe", label: "Sait lire l'arabe (oui/non)" },
  { value: "est_ancien", label: "Ancien élève (oui/non)" },
  { value: "niveau_engagement", label: "Niveau d'engagement (1-5)" },
  { value: "disponibilites", label: "Disponibilités (séparées par ;)" },
  { value: "mode_cours", label: "Mode de cours" },
  { value: "amene_par", label: "Amené par" },
  { value: "commentaires", label: "Commentaires" },
];

function Avatar({ prenom, nom, photo, size = 180 }) {
  const initiales = `${(prenom || "?")[0]}${(nom || "?")[0]}`.toUpperCase();
  const colors = ["#1565c0", "#2e7d32", "#6a1b9a", "#c62828", "#00695c"];
  const bg = colors[(nom?.charCodeAt(0) || 0) % colors.length];
  if (photo)
    return (
      <img src={`${photo}`} alt={`${prenom} ${nom}`}
        style={{
          width: `${size}px`, height: `${size}px`, borderRadius: "12px",
          objectFit: "cover", flexShrink: 0, border: `2px solid ${bg}33`,
        }}
      />
    );
  return (
    <MDBox sx={{
      width: `${size}px`, height: `${size}px`, borderRadius: "12px",
      background: bg, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: `${size * 0.35}px`, flexShrink: 0,
    }}>
      {initiales}
    </MDBox>
  );
}

function Eleves() {
  const navigate = useNavigate();
  const [eleves, setEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [habachiFilter, setHabachiFilter] = useState("tous");
  const isMobile = window.innerWidth < 768;
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : "table");

  const [importModal, setImportModal] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [enseignantsList, setEnseignantsList] = useState([]);
  const [selectedEnseignants, setSelectedEnseignants] = useState([]);
  const [rowEnseignants, setRowEnseignants] = useState({});
  const [rowAnciens, setRowAnciens] = useState({});
  const [rowNiveaux, setRowNiveaux] = useState({});

  useEffect(() => { fetchEleves(); }, []);
  useEffect(() => {
    if (importModal && enseignantsList.length === 0) {
      api.get("/enseignants")
        .then(({ data }) => setEnseignantsList(data))
        .catch((err) => console.error("Erreur chargement enseignants:", err));
    }
  }, [importModal]);

  const fetchEleves = async () => {
    try {
      const { data } = await api.get("/eleves");
      setEleves(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élève ?")) return;
    try {
      await api.delete(`/eleves/${id}`);
      fetchEleves();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length < 2) return;
      const sep = lines[0].includes(";") ? ";" : ",";
      const headers = lines[0].split(sep).map((h) => h.trim().replace(/"/g, "").replace(/coran/gi, "Qour'an"));
      const rows = lines.slice(1).map((line) =>
        line.split(sep).map((v) => v.trim().replace(/"/g, ""))
      );
      setCsvHeaders(headers);
      setCsvData(rows);
      const autoMapping = {};
      headers.forEach((header, i) => {
        const h = header.toLowerCase();
        if (h.includes("prénom") || h === "prenom") autoMapping[i] = "prenom";
        else if (h.includes("nom") && !h.includes("prénom")) autoMapping[i] = "nom";
        else if (h.includes("naissance")) autoMapping[i] = "date_naissance";
        else if (h.includes("sexe") || h.includes("genre")) autoMapping[i] = "sexe";
        else if (h.includes("familiale")) autoMapping[i] = "situation_familiale";
        else if (h.includes("adresse")) autoMapping[i] = "adresse";
        else if (h.includes("ville")) autoMapping[i] = "ville";
        else if (h.includes("postal") || h === "cp") autoMapping[i] = "code_postal";
        else if (h.includes("téléphone") || h.includes("tel")) autoMapping[i] = "telephone";
        else if (h.includes("email") || h.includes("mail")) autoMapping[i] = "email";
        else if (h.includes("langue")) autoMapping[i] = "langue_parlee";
        else if (h.includes("études") || h.includes("etudes")) autoMapping[i] = "niveau_etudes";
        else if (h.includes("classe")) autoMapping[i] = "classe";
        else if (h.includes("profession") || h.includes("métier")) autoMapping[i] = "profession";
        else if (h.includes("quran") || h.includes("quoran") || h.includes("coran") || h.includes("qour")) autoMapping[i] = "niveau_quoran";
        else if (h.includes("arabe") && h.includes("niveau")) autoMapping[i] = "niveau_arabe";
        else if (h.includes("lire")) autoMapping[i] = "sait_lire_arabe";
        else if (h.includes("ancien")) autoMapping[i] = "est_ancien";
        else if (h.includes("engagement") || (h.includes("niveau") && !h.includes("quran") && !h.includes("quoran") && !h.includes("qour") && !h.includes("arabe") && !h.includes("etude"))) autoMapping[i] = "niveau_engagement";
        else if (h.includes("disponib")) autoMapping[i] = "disponibilites";
        else if (h.includes("mode")) autoMapping[i] = "mode_cours";
        else if (h.includes("amené") || h.includes("amene")) autoMapping[i] = "amene_par";
        else if (h.includes("comment") || h.includes("note")) autoMapping[i] = "commentaires";
      });
      setMapping(autoMapping);
      setImportResult(null);
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);
    let success = 0;
    let skipped = 0;
    let errors = 0;

    const { data: elevesExistants } = await api.get("/eleves");

    for (let idx = 0; idx < csvData.length; idx++) {
      const row = csvData[idx];
      if (row.every((v) => !v)) continue;
      const eleve = { langue_parlee: [], disponibilites: [] };

      Object.entries(mapping).forEach(([colIndex, field]) => {
        if (!field) return;
        let value = row[parseInt(colIndex)] || "";
        if (field === "sexe") {
          value = value.toLowerCase().includes("femme") ? "femme" :
                  value.toLowerCase().includes("homme") ? "homme" : "";
        } else if (field === "situation_familiale") {
          value = value.toLowerCase().includes("marié") ? "marie" :
                  value.toLowerCase().includes("divorcé") ? "divorce" : "celibataire";
        } else if (field === "sait_lire_arabe") {
          value = value.toLowerCase().includes("oui") || value === "1";
        } else if (field === "est_ancien") {
          value = value.toLowerCase().includes("oui") || value === "1";
        } else if (field === "niveau_engagement") {
          const v = String(value).toLowerCase().trim();
          if (["1","2","3","4","5"].includes(v)) value = parseInt(v, 10);
          else if (v.includes("commun")) value = 1;
          else if (v.includes("nouveau")) value = 2;
          else if (v.includes("sympa")) value = 3;
          else if (v.includes("cadre")) value = 5;
          else if (v.includes("frere") || v.includes("frère")) value = 4;
          else value = null;
        } else if (field === "langue_parlee" || field === "disponibilites") {
          value = value.split(";").map((v) => v.trim()).filter(Boolean);
        } else if (field === "niveau_quoran" || field === "niveau_arabe") {
          value = value.toLowerCase().includes("avancé") ? "avance" :
                  value.toLowerCase().includes("intermédiaire") ? "intermediaire" : "debutant";
        } else if (field === "mode_cours") {
          value = value.toLowerCase().includes("ligne") ? "enligne" :
                  value.toLowerCase().includes("deux") ? "les_deux" : "presentiel";
        } else if (field === "date_naissance" && value) {
          const parts = value.split("/");
          if (parts.length === 3) {
            value = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
        eleve[field] = value;
      });

      if (rowAnciens[idx] !== undefined) { eleve.est_ancien = rowAnciens[idx]; }
      if (rowNiveaux[idx] !== undefined && rowNiveaux[idx] !== "") { eleve.niveau_engagement = parseInt(rowNiveaux[idx], 10); }
      if (!eleve.prenom) { errors++; continue; }

      const doublon = elevesExistants.find((e) => {
        if (eleve.email && e.email) {
          return e.email.toLowerCase() === eleve.email.toLowerCase();
        }
        return (
          e.prenom?.toLowerCase() === eleve.prenom?.toLowerCase() &&
          e.nom?.toLowerCase() === eleve.nom?.toLowerCase()
        );
      });

      if (doublon) { skipped++; continue; }

      try {
        const { data: nouvelEleve } = await api.post("/eleves", eleve);
        const ensIds = (rowEnseignants[idx] && rowEnseignants[idx].length > 0)
          ? rowEnseignants[idx]
          : selectedEnseignants;
        if (ensIds.length > 0 && nouvelEleve && nouvelEleve.id) {
          try {
            await api.post(`/eleves/${nouvelEleve.id}/enseignants`, {
              enseignant_ids: ensIds,
              source: "import",
            });
          } catch (e) {
            console.error("Lien enseignant echoue pour eleve", nouvelEleve.id, e);
          }
        }
        success++;
      } catch {
        errors++;
      }
    }

    setImportResult({ success, skipped, errors });
    setImporting(false);
    if (success > 0) fetchEleves();
  };

  const filtered = eleves.filter((e) =>
    (habachiFilter === "tous" ||
      (habachiFilter === "habachi" ? e.is_habachi === true : e.is_habachi !== true)) &&
    (`${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.ville?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Gestion des Élèves
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {eleves.length} élève{eleves.length > 1 ? "s" : ""} enregistré{eleves.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton variant="contained" size="medium" component="label"
                    sx={{
                      background: "rgba(255,255,255,0.2)", color: "#fff",
                      fontWeight: 700, borderRadius: "10px",
                      border: "1px solid rgba(255,255,255,0.4)",
                      minWidth: { xs: "auto", sm: "auto" },
                      "&:hover": { background: "rgba(255,255,255,0.3)" },
                    }}>
                    <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>upload_file</Icon>
                    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      Importer CSV
                    </MDBox>
                    <input type="file" accept=".csv" hidden onChange={(e) => {
                      handleCSVUpload(e);
                      setImportModal(true);
                      e.target.value = "";
                    }} />
                  </MDButton>
                  <MDButton variant="contained" size="medium"
                    onClick={() => navigate("/eleves/nouveau")}
                    sx={{
                      background: "#fff", color: "#1565c0",
                      fontWeight: 700, borderRadius: "10px",
                      minWidth: { xs: "auto", sm: "auto" },
                      "&:hover": { background: "#e3f2fd" },
                    }}>
                    <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      Ajouter un élève
                    </MDBox>
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <MDBox display="flex" justifyContent="space-between"
                  alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                  <MDBox sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 } }}>
                    <MDInput fullWidth
                      label="🔍  Rechercher par nom, email, ville..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </MDBox>
                  <MDBox display="flex" gap={0.5} sx={{
                    background: "#f5f5f5", borderRadius: "10px", padding: "3px",
                  }}>
                    {[
                      { value: "tous", label: "Tous" },
                      { value: "habachi", label: "Habachi" },
                      { value: "non_habachi", label: "Non habachi" },
                    ].map((opt) => (
                      <MDBox key={opt.value} onClick={() => setHabachiFilter(opt.value)} sx={{
                        padding: "6px 14px", borderRadius: "8px", cursor: "pointer",
                        fontSize: "13px", fontWeight: 500, transition: "all 0.2s",
                        background: habachiFilter === opt.value ? "#1565c0" : "transparent",
                        color: habachiFilter === opt.value ? "#fff" : "#344767",
                      }}>
                        {opt.label}
                      </MDBox>
                    ))}
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <Tooltip title="Vue tableau">
                      <MDBox onClick={() => setViewMode("table")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "table" ? "#1565c0" : "#f5f5f5",
                        color: viewMode === "table" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">table_rows</Icon>
                      </MDBox>
                    </Tooltip>
                    <Tooltip title="Vue cartes">
                      <MDBox onClick={() => setViewMode("cards")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "cards" ? "#1565c0" : "#f5f5f5",
                        color: viewMode === "cards" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">grid_view</Icon>
                      </MDBox>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column"
                    alignItems="center" justifyContent="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>school</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun élève trouvé
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {search ? "Essayez une autre recherche" : "Commencez par ajouter un élève"}
                    </MDTypography>
                  </MDBox>

                ) : viewMode === "table" ? (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {["Élève", "Ville", "Téléphone", "Langues", "Email", "Actions"].map((h) => (
                            <th key={h} style={{
                              padding: "10px 16px", textAlign: "left",
                              fontSize: 11, fontWeight: 700, color: "#9e9e9e",
                              textTransform: "uppercase", letterSpacing: "0.05em",
                              borderBottom: "2px solid #f5f5f5",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((eleve) => (
                          <tr key={eleve.id}
                            style={{ borderBottom: "1px solid #fafafa" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8faff")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" alignItems="center" gap={1.5}>
                                <Avatar prenom={eleve.prenom} nom={eleve.nom} photo={eleve.photo} size={56} />
                                <MDBox>
                                  <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                    {eleve.prenom} {eleve.nom}
                                  </MDTypography>
                                  {eleve.amene_par && (
                                    <MDTypography variant="caption" color="text" display="block">
                                      Amené par : {eleve.amene_par}
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </MDBox>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {eleve.ville || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {eleve.telephone || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                                {(eleve.langue_parlee || []).slice(0, 2).map((l) => (
                                  <MDBox key={l} sx={{
                                    background: "#e3f2fd", color: "#1565c0",
                                    borderRadius: "10px", padding: "2px 8px",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    {l}
                                  </MDBox>
                                ))}
                                {(eleve.langue_parlee || []).length > 2 && (
                                  <MDBox sx={{
                                    background: "#f5f5f5", color: "#757575",
                                    borderRadius: "10px", padding: "2px 8px",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    +{eleve.langue_parlee.length - 2}
                                  </MDBox>
                                )}
                              </MDBox>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {eleve.email || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" gap={1}>
                                <Tooltip title="Voir la fiche">
                                  <MDBox onClick={() => navigate(`/eleves/${eleve.id}`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#e3f2fd", color: "#1565c0",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#bbdefb" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Modifier">
                                  <MDBox onClick={() => navigate(`/eleves/${eleve.id}/modifier`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#fff8e1", color: "#f57f17",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffecb3" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <MDBox onClick={() => handleDelete(eleve.id)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#ffebee", color: "#c62828",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                                  </MDBox>
                                </Tooltip>
                              </MDBox>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </MDBox>

                ) : (
                  <Grid container spacing={2}>
                    {filtered.map((eleve) => (
                      <Grid item xs={12} sm={6} md={4} key={eleve.id}>
                        <MDBox sx={{
                          background: "#fff", borderRadius: "16px",
                          border: "1px solid #f0f0f0", padding: "20px",
                          transition: "all 0.2s", cursor: "pointer",
                          "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                        }}
                          onClick={() => navigate(`/eleves/${eleve.id}`)}>
                          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar prenom={eleve.prenom} nom={eleve.nom} photo={eleve.photo} size={72} />
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                {eleve.prenom} {eleve.nom}
                              </MDTypography>
                              {eleve.ville && (
                                <MDTypography variant="caption" color="text" display="block">
                                  📍 {eleve.ville}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                          {eleve.telephone && (
                            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                              📞 {eleve.telephone}
                            </MDTypography>
                          )}
                          {eleve.email && (
                            <MDTypography variant="caption" color="text" display="block" mb={1}>
                              ✉️ {eleve.email}
                            </MDTypography>
                          )}
                          {(eleve.langue_parlee || []).length > 0 && (
                            <MDBox display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                              {eleve.langue_parlee.map((l) => (
                                <MDBox key={l} sx={{
                                  background: "#e3f2fd", color: "#1565c0",
                                  borderRadius: "10px", padding: "2px 8px",
                                  fontSize: 11, fontWeight: 600,
                                }}>
                                  {l}
                                </MDBox>
                              ))}
                            </MDBox>
                          )}
                          <MDBox display="flex" gap={1} mt={1} onClick={(e) => e.stopPropagation()}>
                            <MDBox onClick={() => navigate(`/eleves/${eleve.id}/modifier`)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#fff8e1", color: "#f57f17",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffecb3" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>edit</Icon> Modifier
                            </MDBox>
                            <MDBox onClick={() => handleDelete(eleve.id)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffcdd2" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>cancel</Icon> Supprimer
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ── MODALE IMPORT CSV ── */}
      <Modal open={importModal} onClose={() => setImportModal(false)}>
        <MDBox sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 700 },
          maxHeight: "90vh",
          background: "#fff", borderRadius: "20px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          outline: "none", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <MDBox px={3} py={2.5} sx={{
            background: "linear-gradient(135deg, #1565c0, #1976d2)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <MDBox>
              <MDTypography variant="h6" color="white" fontWeight="bold">
                Importer des élèves depuis CSV
              </MDTypography>
              <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                {csvData.length} ligne{csvData.length > 1 ? "s" : ""} détectée{csvData.length > 1 ? "s" : ""}
              </MDTypography>
            </MDBox>
            <MDBox onClick={() => setImportModal(false)} sx={{
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(255,255,255,0.2)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              "&:hover": { background: "rgba(255,255,255,0.35)" },
            }}>
              <Icon sx={{ color: "#fff", fontSize: 18 }}>close</Icon>
            </MDBox>
          </MDBox>

          <MDBox sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {importResult && (
              <MDBox mb={3} p={2} sx={{
                background: importResult.errors === 0 ? "#e8f5e9" : "#fff3e0",
                borderRadius: "10px",
                border: `1px solid ${importResult.errors === 0 ? "#a5d6a7" : "#ffcc02"}`,
              }}>
                <MDTypography variant="body2" fontWeight="bold"
                  sx={{ color: importResult.errors === 0 ? "#2e7d32" : "#e65100" }}>
                  {importResult.success > 0 && `✅ ${importResult.success} élève${importResult.success > 1 ? "s" : ""} importé${importResult.success > 1 ? "s" : ""}`}
                  {importResult.skipped > 0 && ` · ⏭️ ${importResult.skipped} déjà existant${importResult.skipped > 1 ? "s" : ""} (ignoré${importResult.skipped > 1 ? "s" : ""})`}
                  {importResult.errors > 0 && ` · ⚠️ ${importResult.errors} erreur${importResult.errors > 1 ? "s" : ""}`}
                  {importResult.success === 0 && importResult.skipped > 0 && importResult.errors === 0 && " — Aucun nouvel élève à importer !"}
                </MDTypography>
              </MDBox>
            )}

            {csvHeaders.length > 0 && (
              <>
                {/* BLOC CHOIX DES ANCIENS - TOUT EN HAUT DU MODAL D'IMPORT */}
                <MDBox mb={3} p={2} sx={{ border: "1px solid #1A73E8", borderRadius: "10px", background: "#F4F8FF" }}>
                  <MDTypography variant="caption" fontWeight="bold" sx={{
                    textTransform: "uppercase", fontSize: 11, letterSpacing: "0.05em",
                    color: "#1565c0", display: "block", mb: 1.5,
                  }}>
                    Attribuer un statut par élève (ancien + niveau)
                  </MDTypography>
                  <MDBox sx={{ maxHeight: 200, overflowY: "auto", background: "#fff", borderRadius: "8px", border: "1px solid #e0e0e0", p: 1 }}>
                    {csvData.map((row, idx) => {
                      if (row.every((v) => !v)) return null;
                      const prenomIdx = Object.entries(mapping).find(([, v]) => v === "prenom")?.[0];
                      const nomIdx = Object.entries(mapping).find(([, v]) => v === "nom")?.[0];
                      const label = `${prenomIdx !== undefined ? row[prenomIdx] : ""} ${nomIdx !== undefined ? row[nomIdx] : ""}`.trim() || `Élève ligne ${idx + 1}`;
                      return (
                        <div key={idx} style={{
                          display: "flex", alignItems: "center", gap: 10, marginBottom: 6,
                          fontSize: 13, color: "#344767",
                          padding: "6px 8px", borderRadius: "6px",
                          background: rowAnciens[idx] ? "#e3f2fd" : "transparent",
                          transition: "background 0.15s",
                        }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flex: "0 0 auto" }}>
                            <input
                              type="checkbox"
                              checked={!!rowAnciens[idx]}
                              onChange={(e) => setRowAnciens((prev) => ({ ...prev, [idx]: e.target.checked }))}
                              style={{ cursor: "pointer", width: 16, height: 16 }}
                            />
                            Ancien
                          </label>
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                          <select
                            value={rowNiveaux[idx] || ""}
                            onChange={(e) => setRowNiveaux((prev) => ({ ...prev, [idx]: e.target.value }))}
                            style={{
                              flex: "0 0 180px", height: 30, padding: "0 8px",
                              borderRadius: "6px", border: "1px solid #d0d7e2",
                              fontSize: 12, background: "#fff", cursor: "pointer",
                            }}
                          >
                            <option value="">— Niveau —</option>
                            <option value="1">1 — Gens du commun</option>
                            <option value="2">2 — Nouveau</option>
                            <option value="3">3 — Sympathisant</option>
                            <option value="4">4 — Frère</option>
                            <option value="5">5 — Cadre</option>
                          </select>
                        </div>
                      );
                    })}
                  </MDBox>
                </MDBox>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 2,
                }}>
                  Associer les colonnes CSV aux champs de l&apos;application
                </MDTypography>
                <MDBox sx={{
                  background: "#f8faff", borderRadius: "12px",
                  border: "1px solid #e3f2fd", overflow: "hidden", mb: 3,
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Colonne CSV", "Exemple", "Champ application"].map((h) => (
                          <th key={h} style={{
                            padding: "10px 16px", textAlign: "left",
                            fontSize: 11, fontWeight: 700, color: "#9e9e9e",
                            textTransform: "uppercase", letterSpacing: "0.05em",
                            borderBottom: "2px solid #e3f2fd", background: "#f0f7ff",
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvHeaders.map((header, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 16px" }}>
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                              {header}
                            </MDTypography>
                          </td>
                          <td style={{ padding: "10px 16px" }}>
                            <MDTypography variant="caption" color="text">
                              {csvData[0]?.[i] || "—"}
                            </MDTypography>
                          </td>
                          <td style={{ padding: "10px 16px" }}>
                            <select
                              value={mapping[i] || ""}
                              onChange={(e) => setMapping((prev) => ({ ...prev, [i]: e.target.value }))}
                              style={{
                                width: "100%", height: 34, padding: "0 8px",
                                borderRadius: "8px", border: "1px solid #d2d6da",
                                fontSize: 13, outline: "none", background: "#fff",
                              }}
                            >
                              {CHAMPS_BDD.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </MDBox>

                {/* Sélecteur GLOBAL d'enseignants */}
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1,
                }}>
                  Enseignant(s) à assigner à tout le lot
                </MDTypography>
                <MDBox mb={2} sx={{ background: "#f0f7ff", borderRadius: "8px", p: 1.5, border: "1px solid #e3f2fd" }}>
                  <select
                    multiple
                    value={selectedEnseignants.map(String)}
                    onChange={(e) => {
                      const vals = Array.from(e.target.selectedOptions).map((o) => parseInt(o.value, 10));
                      setSelectedEnseignants(vals);
                    }}
                    style={{ width: "100%", minHeight: 90, border: "1px solid #d0d7e2", borderRadius: 6, padding: 6, fontSize: 13 }}
                  >
                    {enseignantsList.map((ens) => (
                      <option key={ens.id} value={ens.id}>
                        {ens.prenom} {ens.nom}
                      </option>
                    ))}
                  </select>
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    Ctrl/Cmd + clic pour sélectionner plusieurs. Appliqué à tous, sauf ajustement par ligne ci-dessous.
                  </MDTypography>
                </MDBox>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1,
                }}>
                  Aperçu — {csvData.length} élève{csvData.length > 1 ? "s" : ""} à importer
                </MDTypography>
                <MDBox sx={{ maxHeight: 320, overflowY: "auto" }}>
                  {csvData.map((row, i) => {
                    if (row.every((v) => !v)) return null;
                    const prenomIdx = Object.entries(mapping).find(([, v]) => v === "prenom")?.[0];
                    const nomIdx = Object.entries(mapping).find(([, v]) => v === "nom")?.[0];
                    const rowSel = rowEnseignants[i];
                    const hasOverride = rowSel && rowSel.length > 0;
                    return (
                      <MDBox key={i} px={2} py={1} mb={0.5}
                        sx={{ background: "#f8faff", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <MDTypography variant="caption" sx={{ color: "#344767", flex: "0 0 40%" }}>
                          {prenomIdx !== undefined ? row[prenomIdx] : "?"}{" "}
                          {nomIdx !== undefined ? row[nomIdx] : ""}
                        </MDTypography>
                        <select
                          multiple
                          value={(hasOverride ? rowSel : []).map(String)}
                          onChange={(e) => {
                            const vals = Array.from(e.target.selectedOptions).map((o) => parseInt(o.value, 10));
                            setRowEnseignants((prev) => ({ ...prev, [i]: vals }));
                          }}
                          style={{ flex: "1", minHeight: 52, border: "1px solid #d0d7e2", borderRadius: 6, padding: 4, fontSize: 12 }}
                        >
                          {enseignantsList.map((ens) => (
                            <option key={ens.id} value={ens.id}>
                              {ens.prenom} {ens.nom}
                            </option>
                          ))}
                        </select>
                        <MDTypography variant="caption" sx={{ flex: "0 0 60px", color: hasOverride ? "#1565c0" : "#9e9e9e", fontSize: 10 }}>
                          {hasOverride ? "spécifique" : "(global)"}
                        </MDTypography>
                      </MDBox>
                    );
                  })}
                </MDBox>
              </>
            )}
          </MDBox>

          <MDBox px={3} py={2} display="flex" gap={2} justifyContent="flex-end"
            sx={{ borderTop: "1px solid #f0f0f0" }}>
            <MDButton variant="outlined" color="secondary" size="small"
              onClick={() => setImportModal(false)}>
              Fermer
            </MDButton>
            {csvData.length > 0 && !importResult && (
              <MDButton size="small" disabled={importing}
                onClick={handleImport}
                sx={{
                  background: "linear-gradient(135deg, #1565c0, #1976d2)",
                  color: "#fff", fontWeight: 700, borderRadius: "8px", px: 3,
                }}>
                {importing
                  ? "Import en cours..."
                  : `Importer ${csvData.length} élève${csvData.length > 1 ? "s" : ""}`}
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </Modal>

      <Footer />
    </DashboardLayout>
  );
}

export default Eleves;